import { VEHICLE_DIMS } from "./dims.gen.js";
import type { VehicleModel } from "./types.js";

/**
 * Real-world measurements of the ingested vehicles (`dims.gen.ts`, written by the ingest post-pass
 * from each model's meta.json). Frame: metres, +Y up, nose +Z, +X = the car's left, origin on the
 * ground under the middle of the wheelbase. Sync, so physics, collisions, the seat and the lamps
 * never wait for a download.
 */
export interface WheelDims {
  x: number;
  y: number;
  z: number;
  r: number;
  w: number;
}

export interface VehicleDims {
  class: string;
  length: number;
  width: number;
  height: number;
  bounds: { min: number[]; max: number[] };
  massKg: number;
  wheelRadius: number;
  wheelbase: number;
  track: number;
  wheels: Record<string, WheelDims>;
  /** The driver's door: hinge = node origin (front edge, mid height); side +1 = +X (left). */
  door: {
    node: string;
    side: number;
    hinge: number[];
    length: number;
    y0: number;
    y1: number;
    cut: boolean;
    tris?: number;
  } | null;
  steering: { x: number; y: number; z: number; r: number } | null;
  /** Driver hip point. */
  seat: number[];
  lamps: { head: number[][]; tail: number[][] };
}

/** The file stem of a catalog model (`vehicles-ccby/suv_generic.glb` -> `suv_generic`). */
export const stemOf = (model: Pick<VehicleModel, "asset">): string =>
  model.asset.file.replace(/\.glb$/i, "");

/** A plausible stand-in for a model without measurements (the legacy Quaternius cars). */
function fallback(length: number): VehicleDims {
  const w = Math.max(0.8, Math.min(2.6, length * 0.42));
  const h = Math.min(3, Math.max(1.1, length * 0.32));
  const wb = length * 0.6;
  const r = 0.33;
  const wheel = (x: number, z: number): WheelDims => ({ x, y: r, z, r, w: 0.22 });
  return {
    class: "sedan",
    length,
    width: w,
    height: h,
    bounds: { min: [-w / 2, 0, -length / 2], max: [w / 2, h, length / 2] },
    massKg: 1400,
    wheelRadius: r,
    wheelbase: wb,
    track: w * 0.78,
    wheels: {
      wheel_fl: wheel(w * 0.39, wb / 2),
      wheel_fr: wheel(-w * 0.39, wb / 2),
      wheel_rl: wheel(w * 0.39, -wb / 2),
      wheel_rr: wheel(-w * 0.39, -wb / 2),
    },
    door: null,
    steering: null,
    seat: [w * 0.17, 0.45, -0.25],
    lamps: {
      head: [
        [w * 0.35, 0.65, length / 2],
        [-w * 0.35, 0.65, length / 2],
      ],
      tail: [
        [w * 0.35, 0.8, -length / 2],
        [-w * 0.35, 0.8, -length / 2],
      ],
    },
  };
}

const fallbacks = new Map<number, VehicleDims>();

/** Measurements of a catalog model (a stand-in of `length` metres when it was never ingested). */
export function dimsOf(model: Pick<VehicleModel, "asset"> | undefined, length = 4.5): VehicleDims {
  const hit = model ? VEHICLE_DIMS[stemOf(model)] : undefined;
  if (hit) return hit;
  let f = fallbacks.get(length);
  if (!f) fallbacks.set(length, (f = fallback(length)));
  return f;
}

/** True when the model's GLB is one of the measured, textured ingested vehicles. */
export const isIngested = (model: Pick<VehicleModel, "asset"> | undefined): boolean =>
  !!model && stemOf(model) in VEHICLE_DIMS;

/** Two-wheelers ride, they are not sat in through a door. */
export const isBike = (d: Pick<VehicleDims, "class">): boolean =>
  d.class === "bike" || d.class === "scooter";
